import attackTacticMapData from './constants/attack-tactic-map.json'

const tacticById: Record<string, string> = attackTacticMapData.tacticById ?? {}
const diagramRows: string[][] = attackTacticMapData.diagramRows ?? []
const legacyStageToTacticId: Record<string, string> =
  attackTacticMapData.legacyStageToTacticId ?? {}

const tacticOrder = diagramRows.flat()

/** 规范为 TAxxxx（大写） */
export function normalizeAttackTacticId (
  raw?: string | number | null,
): string | undefined {
  if (raw == null || raw === '') return undefined
  const text = String(raw).trim()
  const upper = text.toUpperCase()
  if (/^TA\d+$/i.test(upper)) {
    return tacticById[upper] != null ? upper : upper
  }
  const legacy = legacyStageToTacticId[text]
  if (legacy) return legacy
  return undefined
}

/** 展示名称：i18n 优先，其次映射表英文兜底 */
export function getAttackTacticName (
  tacticId?: string | number | null,
  t?: (key: string) => string,
): string | undefined {
  const id = normalizeAttackTacticId(tacticId)
  if (!id) {
    if (tacticId != null && String(tacticId).trim() !== '') return String(tacticId)
    return undefined
  }
  if (t) {
    const i18nKey = `securityEvent.attackTactic.${id}`
    const text = t(i18nKey)
    if (text !== i18nKey) return text
  }
  return tacticById[id]
}

/** 筛选「ATT&CK 攻击阶段」下拉 */
export function getAttackTacticFilterOptions (t?: (key: string) => string) {
  const ids = tacticOrder.length ? tacticOrder : Object.keys(tacticById)
  const seen = new Set<string>()
  return ids
    .filter((id) => {
      if (seen.has(id)) return false
      seen.add(id)
      return true
    })
    .map((id) => ({
      label: getAttackTacticName(id, t) ?? id,
      value: id,
    }))
}

export function getAttackTacticDiagramRows (): string[][] {
  return diagramRows
}

/** MITRE 战术全局顺序（用于攻击链展示） */
export function getAttackTacticChainOrder (): string[] {
  return tacticOrder.length ? [...tacticOrder] : Object.keys(tacticById)
}

/** 按 ATT&CK 顺序排列命中的战术 Id，形成单条攻击链 */
export function getOrderedHitTacticChain (hitStageIds: string[]): string[] {
  const normalized = hitStageIds
    .map((id) => normalizeAttackTacticId(id))
    .filter((id): id is string => !!id)
  if (!normalized.length) return []

  const hitSet = new Set(normalized)
  const ordered = getAttackTacticChainOrder().filter((id) => hitSet.has(id))
  normalized.forEach((id) => {
    if (!ordered.includes(id)) ordered.push(id)
  })
  return ordered
}

function splitAttackTacticTokens (raw: string | number): string[] {
  const text = String(raw).trim()
  if (!text) return []
  const parts = text.split(/[,，、|;/]+/).map((s) => s.trim()).filter(Boolean)
  return parts.length ? parts : [text]
}

export function parseHitAttackTacticIds (
  ...sources: Array<string | number | null | undefined>
): string[] {
  const hits: string[] = []
  for (const src of sources) {
    if (src == null || src === '') continue
    for (const token of splitAttackTacticTokens(src)) {
      const id = normalizeAttackTacticId(token)
      if (id && !hits.includes(id)) hits.push(id)
    }
  }
  return hits
}
